import React, { useState } from "react";
import {
    Box, Paper, Typography, Button, Table, TableHead, TableRow,
    TableCell, TableBody, IconButton, Menu, MenuItem, TablePagination,
    Divider, TextField, Autocomplete, Dialog, DialogTitle, DialogContent,
    DialogActions, ToggleButtonGroup, ToggleButton, ListItemIcon, Collapse, Drawer,
    Avatar, Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Roles from "../../config/roles";
import { useTranslation } from "react-i18next";
import InventoryService from "../../services/InventoryService";

/* ── Section accent colors ── */
const C = {
    entries:   '#427898',
    expenses:  '#F59E0B',
    services:  '#8B5CF6',
    materials: '#10B981',
};

/* ── Shared table styles ── */
const colSx = {
    fontSize: 11, fontWeight: 700, color: '#94A3B8',
    textTransform: 'uppercase', letterSpacing: 0.5,
    borderBottom: '1px solid #EEF2F6',
    py: 1.5, px: 2, bgcolor: '#FAFBFC', whiteSpace: 'nowrap',
};
const cellSx = {
    fontSize: 13, color: '#1E293B',
    borderBottom: '1px solid #F8FAFC',
    py: 1.5, px: 2,
};
const rowSx = {
    '&:hover': { bgcolor: '#F8FAFC' },
    '&:hover .row-actions': { opacity: 1 },
    '& .row-actions': { opacity: 0, transition: 'opacity 0.15s' },
};
const menuPaper = {
    sx: { borderRadius: 1.5, border: '1px solid #EEF2F6', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', minWidth: 140 },
};

const EMPTY_STOCK  = { inventory: null, quantity: '', unitPrice: '', notes: '' };
const EMPTY_DEMAND = { customName: '', customUnit: '', quantity: '', unitPrice: '', notes: '' };

/* ── Accordion section shell ── */
const SectionCard = ({ color, title, stats, addLabel, onAdd, open, onToggle, children }) => (
    <Paper elevation={0} sx={{
        border: '1px solid',
        borderColor: open ? '#DDE3EC' : '#EEF2F6',
        borderRadius: 2,
        bgcolor: '#fff',
        overflow: 'hidden',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        boxShadow: open ? '0 1px 8px rgba(15,23,42,0.05)' : 'none',
    }}>
        {/* ── Header (always visible) ── */}
        <Box
            onClick={onToggle}
            sx={{
                display: 'flex', alignItems: 'center',
                px: 2.5, py: 1.75, cursor: 'pointer', gap: 2,
                bgcolor: open ? '#fff' : '#F8FAFC',
                '&:hover': { bgcolor: '#F8FAFC' },
                userSelect: 'none',
            }}
        >
            {/* Color accent bar */}
            <Box sx={{ width: 3, height: 20, borderRadius: 4, bgcolor: color, flexShrink: 0 }} />

            {/* Title */}
            <Typography fontWeight={700} fontSize={14} color="#0F172A" sx={{ flexShrink: 0 }}>
                {title}
            </Typography>

            {/* Stats badges */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
                {stats.map((s, i) => (
                    <Typography key={i} sx={{
                        fontSize: 12, color: '#64748B', fontWeight: 500,
                        px: 1, py: 0.2, borderRadius: 1, bgcolor: '#F1F5F9',
                        lineHeight: 1.6,
                    }}>
                        {s}
                    </Typography>
                ))}
            </Box>

            {/* Add button */}
            <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                onClick={(e) => { e.stopPropagation(); onAdd(); }}
                sx={{
                    color, bgcolor: `${color}14`, border: `1px solid ${color}30`,
                    textTransform: 'none', fontWeight: 600, fontSize: 12,
                    borderRadius: 1.5, px: 1.5, py: 0.35, minHeight: 0, minWidth: 112,
                    '&:hover': { bgcolor: `${color}22`, border: `1px solid ${color}55` },
                    boxShadow: 'none', flexShrink: 0,
                }}
            >
                {addLabel}
            </Button>

            {/* Chevron */}
            <ExpandMoreIcon sx={{
                fontSize: 18, color: '#94A3B8', flexShrink: 0,
                transition: 'transform 0.2s',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }} />
        </Box>

        {/* ── Collapsible body ── */}
        <Collapse in={open} unmountOnExit>
            <Box sx={{ borderTop: '1px solid #EEF2F6' }}>
                {children}
            </Box>
        </Collapse>
    </Paper>
);

/* ── Smart empty state ── */
const EmptyState = ({ color, message, onAdd, addLabel }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, gap: 1.5 }}>
        <Box sx={{
            width: 36, height: 36, borderRadius: '50%',
            bgcolor: `${color}14`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <AddIcon sx={{ fontSize: 18, color }} />
        </Box>
        <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>{message}</Typography>
        <Button
            size="small" onClick={onAdd}
            sx={{
                color, textTransform: 'none', fontWeight: 600, fontSize: 12,
                border: `1.5px dashed ${color}50`, borderRadius: 1.5, px: 2, py: 0.5,
                '&:hover': { bgcolor: `${color}08`, border: `1.5px dashed ${color}90` },
            }}
        >
            {addLabel}
        </Button>
    </Box>
);

/* ── Invoiced indicator ── */
const InvoicedIcon = ({ yes }) => yes
    ? <CheckCircleIcon sx={{ color: '#22C55E', fontSize: 18 }} />
    : <CancelIcon sx={{ color: '#F87171', fontSize: 18 }} />;

export default function ProjectInfoEntriesExpenses({
    project,
    entries,
    expenses,
    services,
    materials,
    handleAddEntryClick,
    handleMoreProjectEntryClick,
    totalNumberOfEntries,
    totalNumberOfExpenses,
    totalNumberOfServices,
    rowsPerPage,
    page,
    handleChangePage,
    handleChangeRowsPerPage,
    anchorProjectEntryEl,
    handleCloseProjectEntry,
    handleEditProjectEntry,
    handleDeleteProjectEntry,
    selectedEntry,
    anchorProjectExpenseEl,
    handleCloseProjectExpense,
    handleEditProjectExpense,
    handleDeleteProjectExpense,
    handleAddExpenseClick,
    handleMoreProjectExpenseClick,
    selectedExpense,
    handleAddServiceClick,
    anchorProjectServiceEl,
    handleCloseProjectService,
    handleEditProjectService,
    handleDeleteProjectService,
    selectedService,
    handleMoreProjectServiceClick,
    handleAddMaterial,
    handleDeleteMaterial,
    role,
    entryUsers = [],
}) {
    const { t } = useTranslation();

    const userInitials = (userId) => {
        const u = entryUsers.find(u => u.id === userId);
        if (!u) return null;
        return `${u.firstName?.[0] ?? ''}${u.lastName?.[0] ?? ''}`.toUpperCase() || '?';
    };

    const userFullName = (userId) => {
        const u = entryUsers.find(u => u.id === userId);
        return u ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() : null;
    };

    /* ── Section open/close state ── */
    const [sec, setSec] = useState({ entries: true, expenses: true, services: true, materials: true });
    const toggle = (k) => setSec(p => ({ ...p, [k]: !p[k] }));
    const allOpen = Object.values(sec).every(Boolean);

    /* ── Material dialog state ── */
    const [matDialogOpen, setMatDialogOpen] = useState(false);
    const [matMode, setMatMode] = useState('stock');
    const [stockForm, setStockForm]   = useState(EMPTY_STOCK);
    const [demandForm, setDemandForm] = useState(EMPTY_DEMAND);
    const [allInventory, setAllInventory] = useState([]);
    const [inventoryLoaded, setInventoryLoaded] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const openMatDialog = async () => {
        if (!inventoryLoaded) {
            try {
                const data = await InventoryService.getAll();
                setAllInventory(data || []);
                setInventoryLoaded(true);
            } catch { /* silently ignore */ }
        }
        setMatMode('stock');
        setStockForm(EMPTY_STOCK);
        setDemandForm(EMPTY_DEMAND);
        setMatDialogOpen(true);
    };

    const closeMatDialog = () => setMatDialogOpen(false);

    const isStockValid  = () => stockForm.inventory && parseFloat(stockForm.quantity) > 0;
    const isDemandValid = () => demandForm.customName.trim() && parseFloat(demandForm.quantity) > 0 && parseFloat(demandForm.unitPrice) > 0;

    const submitMaterial = async () => {
        if (matMode === 'stock') {
            if (!isStockValid()) return;
            await handleAddMaterial({
                onDemand: false,
                inventoryId: stockForm.inventory.id,
                quantity: parseFloat(stockForm.quantity),
                unitPrice: parseFloat(stockForm.unitPrice) || stockForm.inventory.sellPrice || 0,
                notes: stockForm.notes || null,
            });
        } else {
            if (!isDemandValid()) return;
            await handleAddMaterial({
                onDemand: true,
                customName: demandForm.customName.trim(),
                customUnit: demandForm.customUnit.trim() || null,
                quantity: parseFloat(demandForm.quantity),
                unitPrice: parseFloat(demandForm.unitPrice),
                notes: demandForm.notes || null,
            });
        }
        closeMatDialog();
    };

    /* ── Header stats ── */
    const totalHours = entries.reduce((s, e) => s + (parseFloat(e.workingHours) || 0), 0);
    const entriesStats = [
        `${totalNumberOfEntries || 0} entries`,
        totalHours > 0 ? `${totalHours}h` : null,
        role === Roles.ADMIN && totalHours > 0 && project?.pricePerHour
            ? `CHF ${(totalHours * project.pricePerHour).toFixed(0)}`
            : null,
    ].filter(Boolean);

    const expTotal = expenses.filter(e => !e.isGratis).reduce((s, e) => s + (parseFloat(e.price) || 0), 0);
    const expensesStats = [
        `${expenses.length} expenses`,
        role === Roles.ADMIN && expTotal > 0 ? `CHF ${expTotal.toLocaleString()}` : null,
    ].filter(Boolean);

    const svcTotal = (services || []).reduce((s, sv) => s + ((sv.quantity || 0) * (sv.price || 0)), 0);
    const servicesStats = [
        `${(services || []).length} services`,
        role === Roles.ADMIN && svcTotal > 0 ? `CHF ${svcTotal.toLocaleString()}` : null,
    ].filter(Boolean);

    const matTotal = (materials || []).reduce((s, m) => s + (m.totalPrice || 0), 0);
    const materialsStats = [
        `${(materials || []).length} items`,
        matTotal > 0 ? `CHF ${matTotal.toFixed(0)}` : null,
    ].filter(Boolean);

    /* ── Shared menu item pattern ── */
    const EditMenuItem = ({ onClick, disabled }) => (
        <MenuItem onClick={onClick} disabled={disabled} sx={{ fontSize: 14, gap: 1.5 }}>
            <ListItemIcon sx={{ minWidth: 0 }}><EditIcon sx={{ fontSize: 17, color: '#64748B' }} /></ListItemIcon>
            {t('projectInfoEntriesExpenses.common.edit')}
        </MenuItem>
    );
    const DeleteMenuItem = ({ onClick, disabled }) => (
        <MenuItem onClick={onClick} disabled={disabled} sx={{ fontSize: 14, color: '#EF4444', gap: 1.5 }}>
            <ListItemIcon sx={{ minWidth: 0 }}><DeleteIcon sx={{ fontSize: 17, color: '#EF4444' }} /></ListItemIcon>
            {t('projectInfoEntriesExpenses.common.delete')}
        </MenuItem>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* ── Workspace toolbar ── */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    size="small"
                    onClick={() => setSec({ entries: !allOpen, expenses: !allOpen, services: !allOpen, materials: !allOpen })}
                    sx={{ color: '#94A3B8', textTransform: 'none', fontSize: 12, fontWeight: 500, '&:hover': { color: '#64748B', bgcolor: 'transparent' } }}
                >
                    {allOpen ? 'Collapse all' : 'Expand all'}
                </Button>
            </Box>

            {/* ══════════════════════════════════════════════ */}
            {/* ── Entries & Hours ──────────────────────────  */}
            {/* ══════════════════════════════════════════════ */}
            <SectionCard
                color={C.entries}
                title={t('projectInfoEntriesExpenses.tabs.entriesHours')}
                stats={entriesStats}
                addLabel={t('projectInfoEntriesExpenses.buttons.addEntry')}
                onAdd={handleAddEntryClick}
                open={sec.entries}
                onToggle={() => toggle('entries')}
            >
                {entries.length === 0 ? (
                    <EmptyState
                        color={C.entries}
                        message={t('projectInfoEntriesExpenses.empty.noEntries')}
                        onAdd={handleAddEntryClick}
                        addLabel={t('projectInfoEntriesExpenses.buttons.addEntry')}
                    />
                ) : (
                    <>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={colSx}>{t('projectInfoEntriesExpenses.labels.description')}</TableCell>
                                    <TableCell sx={colSx}>{t('projectInfoEntriesExpenses.labels.hours')}</TableCell>
                                    <TableCell sx={{ ...colSx, textAlign: 'center' }}>Billable</TableCell>
                                    {role === Roles.ADMIN && (
                                        <>
                                            <TableCell sx={colSx}>{t('projectInfoEntriesExpenses.labels.costChf')}</TableCell>
                                            <TableCell sx={{ ...colSx, textAlign: 'center' }}>{t('projectInfoEntriesExpenses.labels.invoiced')}</TableCell>
                                        </>
                                    )}
                                    <TableCell sx={colSx}>Date</TableCell>
                                    <TableCell sx={colSx}>By</TableCell>
                                    <TableCell sx={{ ...colSx, width: 48 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {entries.map((entry, i) => (
                                    <TableRow key={i} sx={rowSx}>
                                        <TableCell sx={{ ...cellSx, fontWeight: 500 }}>{entry.description}</TableCell>
                                        <TableCell sx={cellSx}>{entry.workingHours}h</TableCell>
                                        <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
                                            {entry?.billable !== false
                                                ? <AttachMoneyIcon sx={{ fontSize: 16, color: '#16A34A' }} />
                                                : <Typography sx={{ fontSize: 13, color: '#CBD5E1' }}>—</Typography>}
                                        </TableCell>
                                        {role === Roles.ADMIN && (
                                            <>
                                                <TableCell sx={cellSx}>
                                                    {entry?.workingHours && project?.pricePerHour
                                                        ? `CHF ${(entry.workingHours * project.pricePerHour).toFixed(2)}`
                                                        : '—'}
                                                </TableCell>
                                                <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
                                                    <InvoicedIcon yes={entry.invoiced} />
                                                </TableCell>
                                            </>
                                        )}
                                        <TableCell sx={{ ...cellSx, color: '#64748B' }}>
                                            {entry?.entryDate
                                                ? new Date(entry.entryDate + 'T00:00:00').toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                                : entry?.createdAt
                                                    ? new Date(entry.createdAt).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                                    : '—'}
                                        </TableCell>
                                        <TableCell sx={cellSx}>
                                            {(() => {
                                                const uid = entry?.userId ?? entry?.createdBy;
                                                const initials = uid != null ? userInitials(uid) : null;
                                                const fullName = uid != null ? userFullName(uid) : null;
                                                if (!initials) return <Typography sx={{ fontSize: 12, color: '#CBD5E1' }}>—</Typography>;
                                                return (
                                                    <Tooltip title={fullName || ''} placement="top">
                                                        <Avatar sx={{ width: 26, height: 26, fontSize: 11, fontWeight: 700, bgcolor: '#427898', color: '#fff' }}>
                                                            {initials}
                                                        </Avatar>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell sx={{ ...cellSx, width: 48, pr: 1.5 }}>
                                            <Box className="row-actions" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <IconButton size="small" onClick={(e) => handleMoreProjectEntryClick(e, entry)} sx={{ color: '#94A3B8', '&:hover': { color: C.entries } }}>
                                                    <MoreVertIcon sx={{ fontSize: 17 }} />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={totalNumberOfEntries}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ borderTop: '1px solid #EEF2F6', fontSize: 13 }}
                        />
                    </>
                )}
            </SectionCard>

            <Menu anchorEl={anchorProjectEntryEl} open={Boolean(anchorProjectEntryEl)} onClose={handleCloseProjectEntry} PaperProps={menuPaper}>
                <EditMenuItem onClick={() => { handleEditProjectEntry(selectedEntry); handleCloseProjectEntry(); }} disabled={selectedEntry?.invoiced} />
                <Divider />
                <DeleteMenuItem onClick={() => { handleDeleteProjectEntry(selectedEntry); handleCloseProjectEntry(); }} disabled={selectedEntry?.invoiced} />
            </Menu>

            {/* ══════════════════════════════════════════════ */}
            {/* ── Expenses ─────────────────────────────────  */}
            {/* ══════════════════════════════════════════════ */}
            <SectionCard
                color={C.expenses}
                title={t('projectInfoEntriesExpenses.tabs.expenses')}
                stats={expensesStats}
                addLabel={t('projectInfoEntriesExpenses.buttons.addExpense')}
                onAdd={handleAddExpenseClick}
                open={sec.expenses}
                onToggle={() => toggle('expenses')}
            >
                {expenses.length === 0 ? (
                    <EmptyState
                        color={C.expenses}
                        message={t('projectInfoEntriesExpenses.empty.noExpenses')}
                        onAdd={handleAddExpenseClick}
                        addLabel={t('projectInfoEntriesExpenses.buttons.addExpense')}
                    />
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={colSx}>{t('projectInfoEntriesExpenses.labels.description')}</TableCell>
                                {role === Roles.ADMIN && (
                                    <>
                                        <TableCell sx={colSx}>{t('projectInfoEntriesExpenses.labels.priceChf')}</TableCell>
                                        {(
                                            <TableCell sx={{ ...colSx, textAlign: 'center' }}>{t('projectInfoEntriesExpenses.labels.invoiced')}</TableCell>
                                        )}
                                    </>
                                )}
                                <TableCell sx={{ ...colSx, width: 48 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {expenses.map((expense, i) => (
                                <TableRow key={i} sx={rowSx}>
                                    <TableCell sx={{ ...cellSx, fontWeight: 500 }}>{expense.description}</TableCell>
                                    {role === Roles.ADMIN && (
                                        <>
                                            <TableCell sx={cellSx}>
                                                {expense?.isGratis ? (
                                                    <Typography sx={{ color: '#22C55E', fontWeight: 600, fontSize: 13 }}>
                                                        {t('projectInfoEntriesExpenses.common.free')}
                                                    </Typography>
                                                ) : `CHF ${expense?.price?.toLocaleString()}`}
                                            </TableCell>
                                            {(
                                                <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
                                                    <InvoicedIcon yes={expense?.invoiced} />
                                                </TableCell>
                                            )}
                                        </>
                                    )}
                                    <TableCell sx={{ ...cellSx, width: 48, pr: 1.5 }}>
                                        <Box className="row-actions" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <IconButton size="small" onClick={(e) => handleMoreProjectExpenseClick(e, expense)} sx={{ color: '#94A3B8', '&:hover': { color: C.expenses } }}>
                                                <MoreVertIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </SectionCard>

            <Menu anchorEl={anchorProjectExpenseEl} open={Boolean(anchorProjectExpenseEl)} onClose={handleCloseProjectExpense} PaperProps={menuPaper}>
                <EditMenuItem onClick={() => { handleEditProjectExpense(selectedExpense); handleCloseProjectExpense(); }} disabled={selectedExpense?.invoiced} />
                <Divider />
                <DeleteMenuItem onClick={() => { handleDeleteProjectExpense(selectedExpense); handleCloseProjectExpense(); }} disabled={selectedExpense?.invoiced} />
            </Menu>

            {/* ══════════════════════════════════════════════ */}
            {/* ── Services ─────────────────────────────────  */}
            {/* ══════════════════════════════════════════════ */}
            <SectionCard
                color={C.services}
                title={t('projectInfoEntriesExpenses.tabs.services')}
                stats={servicesStats}
                addLabel={t('projectInfoEntriesExpenses.buttons.addService')}
                onAdd={handleAddServiceClick}
                open={sec.services}
                onToggle={() => toggle('services')}
            >
                {(services || []).length === 0 ? (
                    <EmptyState
                        color={C.services}
                        message={t('projectInfoEntriesExpenses.empty.noServices')}
                        onAdd={handleAddServiceClick}
                        addLabel={t('projectInfoEntriesExpenses.buttons.addService')}
                    />
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={colSx}>{t('projectInfoEntriesExpenses.labels.service')}</TableCell>
                                <TableCell sx={colSx}>{t('projectInfoEntriesExpenses.labels.subservice')}</TableCell>
                                <TableCell sx={colSx}>{t('projectInfoEntriesExpenses.labels.quantity')}</TableCell>
                                {role === Roles.ADMIN && (
                                    <>
                                        <TableCell sx={colSx}>{t('projectInfoEntriesExpenses.labels.pricePerUnit')}</TableCell>
                                        <TableCell sx={colSx}>{t('projectInfoEntriesExpenses.labels.totalPrice')}</TableCell>
                                        {(
                                            <TableCell sx={{ ...colSx, textAlign: 'center' }}>{t('projectInfoEntriesExpenses.labels.invoiced')}</TableCell>
                                        )}
                                    </>
                                )}
                                <TableCell sx={colSx}>{t('projectInfoEntriesExpenses.labels.description')}</TableCell>
                                <TableCell sx={{ ...colSx, width: 48 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(services || []).map((sv, i) => (
                                <TableRow key={i} sx={rowSx}>
                                    <TableCell sx={{ ...cellSx, fontWeight: 500 }}>{sv?.serviceName}</TableCell>
                                    <TableCell sx={cellSx}>{sv?.subServiceName}</TableCell>
                                    <TableCell sx={cellSx}>{sv?.quantity}</TableCell>
                                    {role === Roles.ADMIN && (
                                        <>
                                            <TableCell sx={cellSx}>{sv?.price} CHF/{sv?.unit}</TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{sv?.quantity * sv?.price} CHF</TableCell>
                                            {(
                                                <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
                                                    <InvoicedIcon yes={sv?.invoiced} />
                                                </TableCell>
                                            )}
                                        </>
                                    )}
                                    <TableCell sx={{ ...cellSx, color: '#64748B' }}>{sv?.description}</TableCell>
                                    <TableCell sx={{ ...cellSx, width: 48, pr: 1.5 }}>
                                        <Box className="row-actions" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <IconButton size="small" onClick={(e) => handleMoreProjectServiceClick(e, sv)} sx={{ color: '#94A3B8', '&:hover': { color: C.services } }}>
                                                <MoreVertIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </SectionCard>

            <Menu anchorEl={anchorProjectServiceEl} open={Boolean(anchorProjectServiceEl)} onClose={handleCloseProjectService} PaperProps={menuPaper}>
                <EditMenuItem onClick={() => { handleEditProjectService(selectedService); handleCloseProjectService(); }} disabled={selectedService?.invoiced} />
                <Divider />
                <DeleteMenuItem onClick={() => { handleDeleteProjectService(selectedService); handleCloseProjectService(); }} disabled={selectedService?.invoiced} />
            </Menu>

            {/* ══════════════════════════════════════════════ */}
            {/* ── Materials ────────────────────────────────  */}
            {/* ══════════════════════════════════════════════ */}
            <SectionCard
                color={C.materials}
                title="Materials"
                stats={materialsStats}
                addLabel="Add Material"
                onAdd={openMatDialog}
                open={sec.materials}
                onToggle={() => toggle('materials')}
            >
                {(materials || []).length === 0 ? (
                    <EmptyState
                        color={C.materials}
                        message="No materials assigned yet"
                        onAdd={openMatDialog}
                        addLabel="Add Material"
                    />
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={colSx}>Item</TableCell>
                                <TableCell sx={colSx}>Qty</TableCell>
                                <TableCell sx={colSx}>Unit price</TableCell>
                                <TableCell sx={colSx}>Total</TableCell>
                                {project?.isFixedPrice === false && <TableCell sx={{ ...colSx, textAlign: 'center' }}>Invoiced</TableCell>}
                                <TableCell sx={{ ...colSx, width: 48 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(materials || []).map(m => (
                                <TableRow key={m.id} sx={rowSx}>
                                    <TableCell sx={{ ...cellSx, fontWeight: 500 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {m.onDemand ? (
                                                <>
                                                    <span>{m.customName}</span>
                                                    {m.customUnit && (
                                                        <Typography component="span" variant="caption" sx={{ color: '#94A3B8' }}>{m.customUnit}</Typography>
                                                    )}
                                                    <Box sx={{ px: 0.75, lineHeight: '18px', borderRadius: '9px', bgcolor: '#fef3c7', color: '#92400e', fontSize: 10, fontWeight: 600, display: 'inline-block' }}>
                                                        On Demand
                                                    </Box>
                                                </>
                                            ) : (
                                                <>
                                                    <span>{m.inventoryName}</span>
                                                    {m.unitOfMeasure && (
                                                        <Typography component="span" variant="caption" sx={{ color: '#94A3B8' }}>{m.unitOfMeasure}</Typography>
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ ...cellSx, color: '#64748B' }}>
                                        {m.quantity} {m.onDemand ? (m.customUnit || '') : (m.unitOfMeasure || '')}
                                    </TableCell>
                                    <TableCell sx={{ ...cellSx, color: '#64748B' }}>CHF {m.unitPrice?.toFixed(2)}</TableCell>
                                    <TableCell sx={{ ...cellSx, fontWeight: 600 }}>CHF {m.totalPrice?.toFixed(2)}</TableCell>
                                    {(
                                        <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
                                            <InvoicedIcon yes={m.invoiced} />
                                        </TableCell>
                                    )}
                                    <TableCell sx={{ ...cellSx, width: 48, pr: 1.5 }}>
                                        <Box className="row-actions" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setDeleteTarget(m)}
                                                disabled={m.invoiced}
                                                sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444' } }}
                                            >
                                                <CloseIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </SectionCard>

            {/* ── Add Material Drawer ── */}
            <Drawer
                anchor="right"
                open={matDialogOpen}
                onClose={closeMatDialog}
                PaperProps={{ sx: { width: { xs: '100vw', sm: 480 }, boxShadow: '-4px 0 32px rgba(15,23,42,0.12)' } }}
                slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Accent bar */}
                    <Box sx={{ height: 3, bgcolor: C.materials, flexShrink: 0 }} />

                    {/* Header */}
                    <Box sx={{
                        px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #EEF2F6',
                        flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    }}>
                        <Box>
                            <Typography fontWeight={700} fontSize={16} color="#0F172A">Add Material</Typography>
                            <Typography fontSize={12} color="#94A3B8" mt={0.3}>Materials</Typography>
                        </Box>
                        <IconButton size="small" onClick={closeMatDialog} sx={{ color: '#94A3B8', mt: -0.25 }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Source toggle — pinned below header */}
                    <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0 }}>
                        <ToggleButtonGroup
                            value={matMode} exclusive
                            onChange={(_, v) => { if (v) setMatMode(v); }}
                            size="small" sx={{ width: '100%' }}
                        >
                            <ToggleButton value="stock" sx={{ flex: 1, textTransform: 'none', fontWeight: 600, gap: 0.75 }}>
                                <Inventory2Icon fontSize="small" /> From Stock
                            </ToggleButton>
                            <ToggleButton value="ondemand" sx={{ flex: 1, textTransform: 'none', fontWeight: 600, gap: 0.75 }}>
                                <ShoppingCartIcon fontSize="small" /> On Demand
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Scrollable body */}
                    <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
                        {matMode === 'stock' ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Autocomplete
                                    options={allInventory}
                                    getOptionLabel={opt => `${opt.name}${opt.category ? ` (${opt.category})` : ''}`}
                                    value={stockForm.inventory}
                                    onChange={(_, v) => setStockForm(p => ({ ...p, inventory: v, unitPrice: v ? String(v.sellPrice || '') : '' }))}
                                    renderInput={params => <TextField {...params} label="Inventory item *" size="small" />}
                                />
                                {stockForm.inventory && stockForm.inventory.currentStock <= stockForm.inventory.minStockLevel && stockForm.inventory.minStockLevel > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 1.25, borderRadius: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
                                        <WarningAmberIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                                        <Typography variant="caption" sx={{ color: '#92400e' }}>
                                            Low stock — only {stockForm.inventory.currentStock} {stockForm.inventory.unitOfMeasure || ''} remaining
                                        </Typography>
                                    </Box>
                                )}
                                {stockForm.inventory && (
                                    <Box sx={{ px: 1.5, py: 1.25, borderRadius: 1.5, bgcolor: `${C.materials}0E`, border: `1px solid ${C.materials}25`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography fontSize={12} color="#064E3B" fontWeight={500}>Current stock</Typography>
                                        <Typography fontSize={13} fontWeight={700} color={C.materials}>
                                            {stockForm.inventory.currentStock} {stockForm.inventory.unitOfMeasure || ''}
                                        </Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <TextField
                                        label={`Quantity${stockForm.inventory?.unitOfMeasure ? ` (${stockForm.inventory.unitOfMeasure})` : ''} *`}
                                        type="number" size="small" sx={{ flex: 1 }}
                                        value={stockForm.quantity}
                                        onChange={e => setStockForm(p => ({ ...p, quantity: e.target.value }))}
                                    />
                                    <TextField
                                        label="Unit price"
                                        type="number" size="small" sx={{ flex: 1 }}
                                        value={stockForm.unitPrice}
                                        onChange={e => setStockForm(p => ({ ...p, unitPrice: e.target.value }))}
                                        InputProps={{ startAdornment: <Box component="span" sx={{ pr: 0.75, color: '#94A3B8', fontSize: 13 }}>CHF</Box> }}
                                    />
                                </Box>
                                <TextField label="Notes (optional)" size="small" multiline rows={2}
                                    value={stockForm.notes}
                                    onChange={e => setStockForm(p => ({ ...p, notes: e.target.value }))}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <TextField label="Material name *" size="small" sx={{ flex: 2 }}
                                        value={demandForm.customName}
                                        onChange={e => setDemandForm(p => ({ ...p, customName: e.target.value }))}
                                        autoFocus
                                    />
                                    <TextField label="Unit (pcs, m², kg…)" size="small" sx={{ flex: 1 }}
                                        value={demandForm.customUnit}
                                        onChange={e => setDemandForm(p => ({ ...p, customUnit: e.target.value }))}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <TextField label="Quantity *" type="number" size="small" sx={{ flex: 1 }}
                                        value={demandForm.quantity}
                                        onChange={e => setDemandForm(p => ({ ...p, quantity: e.target.value }))}
                                    />
                                    <TextField label="Unit price *" type="number" size="small" sx={{ flex: 1 }}
                                        value={demandForm.unitPrice}
                                        onChange={e => setDemandForm(p => ({ ...p, unitPrice: e.target.value }))}
                                        InputProps={{ startAdornment: <Box component="span" sx={{ pr: 0.75, color: '#94A3B8', fontSize: 13 }}>CHF</Box> }}
                                    />
                                </Box>
                                {demandForm.quantity && demandForm.unitPrice && (
                                    <Box sx={{ px: 1.5, py: 1.25, borderRadius: 1.5, bgcolor: `${C.materials}0E`, border: `1px solid ${C.materials}25`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography fontSize={12} color="#064E3B" fontWeight={500}>Total</Typography>
                                        <Typography fontSize={13} fontWeight={700} color={C.materials}>
                                            CHF {(parseFloat(demandForm.quantity) * parseFloat(demandForm.unitPrice)).toFixed(2)}
                                        </Typography>
                                    </Box>
                                )}
                                <TextField label="Notes (optional)" size="small" multiline rows={2}
                                    value={demandForm.notes}
                                    onChange={e => setDemandForm(p => ({ ...p, notes: e.target.value }))}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Sticky footer */}
                    <Box sx={{
                        px: 3, py: 2.5, flexShrink: 0,
                        borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC',
                        display: 'flex', justifyContent: 'flex-end', gap: 1.5,
                    }}>
                        <Button onClick={closeMatDialog} sx={{ textTransform: 'none', color: '#64748B', fontWeight: 500 }}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={submitMaterial}
                            disabled={matMode === 'stock' ? !isStockValid() : !isDemandValid()}
                            sx={{ bgcolor: C.materials, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', px: 2.5, '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}
                        >
                            Add Material
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            {/* ── Remove Material Confirmation ── */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #EEF2F6' }}>Remove Material</DialogTitle>
                <DialogContent sx={{ pt: 2.5 }}>
                    <Typography variant="body2" sx={{ color: '#4A5568' }}>
                        {deleteTarget?.onDemand
                            ? `Remove "${deleteTarget?.customName}"? This on-demand item will be deleted.`
                            : `Remove "${deleteTarget?.inventoryName}"? ${deleteTarget?.quantity} ${deleteTarget?.unitOfMeasure || 'units'} will be returned to inventory.`}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: '1px solid #EEF2F6' }}>
                    <Button onClick={() => setDeleteTarget(null)} sx={{ color: '#64748B', textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained" color="error"
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none' }}
                        onClick={() => { handleDeleteMaterial(deleteTarget.id); setDeleteTarget(null); }}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
